import { Delete, Edit } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { DateTime } from 'luxon'
import { Theme } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import React, { useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import { Redirect } from 'wouter'
import { AlertSeverity, ContactMethodType, WeekdayFilter } from '../../schema'
import FormDialog from '../dialogs/FormDialog'
import { GenericError } from '../error-pages'
import Spinner from '../loading/components/Spinner'
import Diff from '../util/Diff'
import { SlackChannelSelect } from '../selection/SlackChannelSelect'
import ServiceNotificationRuleList from './ServiceNotificationRuleList'
import ServiceSlackListCreateDialog from './ServiceSlackListCreateDialog'
import ServiceSlackListDeleteDialog from './ServiceSlackListDeleteDialog'
import ServiceSlackListEditDialog from './ServiceSlackListEditDialog'
import { DurationField } from '../util/DurationField'

const query = gql`
  query serviceDedupKeyExpiry($serviceID: ID!) {
    service(id: $serviceID) {
      id
      dedupKeyExpiry
      autoCloseStrategy
      autoCloseThreshold
      notificationRules {
        id
        delayMinutes
        contactMethodType
        alertSeverity
        notifyAnytime
        startTime
        endTime
        timezone
        weekdayFilter
      }
      slackLists {
        id
        slackListID
        name
      }
      slackChannelID
    }
  }
`

const mutation = gql`
  mutation updateServiceSettings($input: UpdateServiceInput!) {
    updateService(input: $input)
  }
`

const createMutation = gql`
  mutation ($input: CreateServiceNotificationRuleInput!) {
    createServiceNotificationRule(input: $input) {
      id
    }
  }
`

const deleteMutation = gql`
  mutation ($id: ID!) {
    deleteServiceNotificationRule(id: $id)
  }
`

const channelNameQuery = gql`
  query ($id: ID!) {
    slackChannel(id: $id) {
      id
      name
    }
  }
`

const useStyles = makeStyles((theme: Theme) => ({
  accordionDetails: {
    padding: 0,
    display: 'block',
  },
  heading: {
    fontSize: '1.1rem',
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
    flexGrow: 1,
  },
  changeChip: {
    justifyContent: 'flex-end',
  },
  listItem: {
    minHeight: '71px',
    padding: '1em',
  },
  listItemText: {
    maxWidth: '100%',
  },
  listItemPadding: {
    paddingTop: theme.spacing(2),
  },
  inputAction: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: theme.spacing(2),
  },
  inputRow: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
    maxWidth: '500px',
  },
  configuredChannelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: '500px',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  inputItem: {
    flex: 1,
    flexBasis: '50%',
  },
}))

interface NotificationRule {
  id: string
  contactMethodType: ContactMethodType
  alertSeverity: AlertSeverity
  delayMinutes: number
  notifyAnytime: boolean
  startTime: string
  endTime: string
  timezone: string
  weekdayFilter: WeekdayFilter
  deleted?: boolean
}

interface NotificationRuleInput {
  contactMethodType: ContactMethodType
  alertSeverity: AlertSeverity
  delayMinutes: number
  notifyAnytime: boolean
  startTime: string
  endTime: string
  timezone: string
  weekdayFilter: WeekdayFilter
}

export default function ServiceSettings({ serviceID }: { serviceID: string }) {
  const classes = useStyles()
  const [{ data, fetching, error }] = useQuery({
    query,
    variables: { serviceID },
  })

  const [updateMsg, setUpdateMsg] = useState('')
  const [{ fetching: saving }, updateServiceSettings] = useMutation(mutation)
  const [, createRule] = useMutation(createMutation)
  const [, deleteRule] = useMutation(deleteMutation)

  const expiryMinutes = data?.service?.dedupKeyExpiry || 720
  const predefinedValues = [60, 360, 720, 1440, 10080]
  const isPredefined = predefinedValues.includes(expiryMinutes)

  // Staged changes state
  const [stagedValue, setStagedValue] = useState<number | null>(null)
  const [stagedRules, setStagedRules] = useState<NotificationRule[] | null>(
    null,
  )
  const [stagedSlackChannelID, setStagedSlackChannelID] = useState<
    string | null
  >(null)
  const [stagedAutoCloseStrategy, setStagedAutoCloseStrategy] = useState<
    string | null
  >(null)
  const [stagedAutoCloseThreshold, setStagedAutoCloseThreshold] = useState<
    string | null
  >(null)

  const [isCustom, setIsCustom] = useState(!isPredefined)
  const [customValue, setCustomValue] = useState(expiryMinutes.toString())
  const [confirm, setConfirm] = useState(false)
  const [expanded, setExpanded] = useState<string | false>('dedup')

  if (fetching && !data) return <Spinner />
  if (error) return <GenericError error={error.message} />
  if (!data?.service) return <Redirect to={`/services/${serviceID}`} />

  // Current value is either staged value or DB value
  const currentValue = stagedValue !== null ? stagedValue : expiryMinutes
  const currentSlackChannelID =
    stagedSlackChannelID !== null
      ? stagedSlackChannelID
      : data?.service?.slackChannelID || ''
  const hasChanges =
    stagedValue !== null ||
    stagedRules !== null ||
    stagedSlackChannelID !== null ||
    stagedAutoCloseStrategy !== null ||
    stagedAutoCloseThreshold !== null

  const originalRules: NotificationRule[] =
    data?.service?.notificationRules || []
  const currentRules = stagedRules || originalRules

  const handleDropdownChange = (value: string) => {
    if (value === 'custom') {
      setIsCustom(true)
      setCustomValue(currentValue.toString())
      return
    }

    const numValue = parseInt(value, 10)
    setIsCustom(false)
    setStagedValue(numValue === expiryMinutes ? null : numValue)
  }

  const handleCustomChange = (value: string) => {
    setCustomValue(value)
    const numValue = parseInt(value, 10)
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 90000) {
      setStagedValue(numValue === expiryMinutes ? null : numValue)
    }
  }

  const handleRuleAdd = (input: NotificationRuleInput) => {
    const rules = stagedRules ? [...stagedRules] : [...originalRules]
    rules.push({
      ...input,
      id: 'temp-' + Date.now(),
    })
    setStagedRules(rules)
  }

  const handleRuleDelete = (id: string) => {
    const rules = stagedRules ? [...stagedRules] : [...originalRules]
    if (id.startsWith('temp-')) {
      setStagedRules(rules.filter((r) => r.id !== id))
    } else {
      setStagedRules(
        rules.map((r) => {
          if (r.id === id) return { ...r, deleted: true }
          return r
        }),
      )
    }
  }

  const handleAutoCloseStrategyChange = (value: string) => {
    const isOriginal = value === (data?.service?.autoCloseStrategy || 'ALWAYS')
    setStagedAutoCloseStrategy(isOriginal ? null : value)

    if (value === 'THRESHOLD' && stagedAutoCloseThreshold === null) {
      // If we switch to THRESHOLD but don't have a staged threshold yet, we need to ensure
      // it gets sent in the mutation if the original strategy wasn't THRESHOLD.
      if (data?.service?.autoCloseStrategy !== 'THRESHOLD') {
        setStagedAutoCloseThreshold(data?.service?.autoCloseThreshold || 'P3D')
      }
    } else if (value !== 'THRESHOLD') {
      // If we switch away from THRESHOLD, clear any staged threshold.
      setStagedAutoCloseThreshold(null)
    }
  }

  const handleAutoCloseThresholdChange = (value: string) => {
    setStagedAutoCloseThreshold(
      value === (data?.service?.autoCloseThreshold || 'P3D') ? null : value,
    )
  }

  const handleSave = async () => {
    if (!hasChanges) return
    setUpdateMsg('')

    const promises = []

    if (
      stagedValue !== null ||
      stagedSlackChannelID !== null ||
      stagedAutoCloseStrategy !== null ||
      stagedAutoCloseThreshold !== null
    ) {
      const input: any = { id: serviceID }
      if (stagedValue !== null) input.dedupKeyExpiry = stagedValue
      if (stagedSlackChannelID !== null)
        input.slackChannelID = stagedSlackChannelID
      if (stagedAutoCloseStrategy !== null)
        input.autoCloseStrategy = stagedAutoCloseStrategy
      if (stagedAutoCloseThreshold !== null)
        input.autoCloseThreshold = stagedAutoCloseThreshold

      promises.push(
        updateServiceSettings({ input }, { additionalTypenames: ['Service'] }),
      )
    }

    if (stagedRules !== null) {
      // Find added rules
      const added = stagedRules.filter((r) => r.id.startsWith('temp-'))
      // Find deleted rules
      const deleted = stagedRules.filter((r) => r.deleted)

      added.forEach((r: NotificationRule) => {
        promises.push(
          createRule(
            {
              input: {
                serviceID,
                delayMinutes: r.delayMinutes,
                contactMethodType: r.contactMethodType,
                alertSeverity: r.alertSeverity,
                notifyAnytime: r.notifyAnytime,
                startTime: DateTime.fromISO(r.startTime).toFormat('HH:mm'),
                endTime: DateTime.fromISO(r.endTime).toFormat('HH:mm'),
                timezone: r.timezone,
                weekdayFilter: r.weekdayFilter,
              },
            },
            { additionalTypenames: ['Service', 'ServiceNotificationRule'] },
          ),
        )
      })

      deleted.forEach((r: NotificationRule) => {
        promises.push(
          deleteRule(
            { id: r.id },
            { additionalTypenames: ['Service', 'ServiceNotificationRule'] },
          ),
        )
      })
    }

    const results = await Promise.all(promises)
    const err = results.find((r: any) => r.error)
    if (err) {
      setUpdateMsg(err.error?.message || 'Error saving changes.')
    } else {
      setUpdateMsg('Settings saved successfully.')
      setStagedValue(null)
      setStagedRules(null)
      setStagedSlackChannelID(null)
      setStagedAutoCloseStrategy(null)
      setStagedAutoCloseThreshold(null)
      setConfirm(false)
    }
  }

  const handleReset = () => {
    setStagedValue(null)
    setStagedRules(null)
    setStagedSlackChannelID(null)
    setStagedAutoCloseStrategy(null)
    setStagedAutoCloseThreshold(null)
    setIsCustom(!isPredefined)
    setCustomValue(expiryMinutes.toString())
    setUpdateMsg('')
  }

  const displayValue = isCustom
    ? 'custom'
    : predefinedValues.includes(currentValue)
      ? currentValue
      : 'custom'

  // A simplistic check, strictly distinct from original is better but this is a quick heuristic
  // Better heuristic:
  const rulesChanged =
    stagedRules !== null &&
    (stagedRules.length !== originalRules.length ||
      stagedRules.some((r) => r.id.startsWith('temp-') || r.deleted) ||
      originalRules.some((r) => !stagedRules.find((sr) => sr.id === r.id)))

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} container justifyContent='flex-end'>
        <ButtonGroup variant='outlined'>
          <Button data-cy='reset' disabled={!hasChanges} onClick={handleReset}>
            Reset
          </Button>
          <Button
            data-cy='save'
            disabled={!hasChanges}
            onClick={() => setConfirm(true)}
          >
            Save
          </Button>
        </ButtonGroup>
      </Grid>
      {confirm && hasChanges && (
        <FormDialog
          title='Apply Changes?'
          onClose={() => setConfirm(false)}
          onSubmit={handleSave}
          primaryActionLabel='Confirm'
          loading={saving}
          form={
            <List data-cy='confirmation-diff'>
              {stagedValue !== null && (
                <ListItem divider>
                  <ListItemText
                    disableTypography
                    secondary={
                      <Diff
                        oldValue={expiryMinutes.toString()}
                        newValue={stagedValue.toString()}
                        type='chars'
                      />
                    }
                  >
                    <Typography>Deduplication Key Expiry</Typography>
                  </ListItemText>
                </ListItem>
              )}
              {rulesChanged && (
                <ListItem divider>
                  <ListItemText
                    disableTypography
                    secondary={
                      <Typography variant='body2'>
                        {(() => {
                          if (!stagedRules) return ''
                          const added = stagedRules.filter((r) =>
                            r.id.startsWith('temp-'),
                          ).length
                          const deleted = stagedRules.filter(
                            (r) => r.deleted,
                          ).length
                          const parts = []
                          if (added) parts.push(`+${added}`)
                          if (deleted) parts.push(`-${deleted}`)
                          return `Notification Rules updated (${parts.join(
                            ', ',
                          )} rules)`
                        })()}
                      </Typography>
                    }
                  >
                    <Typography>Notification Rules</Typography>
                  </ListItemText>
                </ListItem>
              )}
              {stagedSlackChannelID !== null && (
                <ListItem divider>
                  <ListItemText
                    disableTypography
                    secondary={
                      <Diff
                        oldValue={data?.service?.slackChannelID || 'None'}
                        newValue={stagedSlackChannelID || 'None'}
                        type='words'
                      />
                    }
                  >
                    <Typography>Slack Channel</Typography>
                  </ListItemText>
                </ListItem>
              )}
              {stagedAutoCloseStrategy !== null && (
                <ListItem divider>
                  <ListItemText
                    disableTypography
                    secondary={
                      <Diff
                        oldValue={data?.service?.autoCloseStrategy || 'ALWAYS'}
                        newValue={stagedAutoCloseStrategy}
                        type='words'
                      />
                    }
                  >
                    <Typography>Auto-Close Strategy</Typography>
                  </ListItemText>
                </ListItem>
              )}
              {stagedAutoCloseThreshold !== null && (
                <ListItem divider>
                  <ListItemText
                    disableTypography
                    secondary={
                      <Diff
                        oldValue={data?.service?.autoCloseThreshold || 'P3D'}
                        newValue={stagedAutoCloseThreshold}
                        type='words'
                      />
                    }
                  >
                    <Typography>Auto-Close Threshold</Typography>
                  </ListItemText>
                </ListItem>
              )}
            </List>
          }
        />
      )}
      <Grid item xs={12}>
        <Accordion
          expanded={expanded === 'dedup'}
          onChange={() => setExpanded(expanded === 'dedup' ? false : 'dedup')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>
              Alert Deduplication
            </Typography>
            <Typography className={classes.secondaryHeading}>
              Configure how long the system identifies duplicate alerts.
            </Typography>
            {stagedValue !== null && (
              <Chip
                className={classes.changeChip}
                size='small'
                label='1 unsaved change'
              />
            )}
          </AccordionSummary>
          <Divider />
          <AccordionDetails className={classes.accordionDetails}>
            <List>
              <ListItem
                className={classes.listItem}
                sx={{
                  bgcolor:
                    stagedValue !== null ? 'action.selected' : 'transparent',
                }}
              >
                <div style={{ width: '100%' }}>
                  <ListItemText
                    className={classes.listItemText}
                    primary='Deduplication Key Expiry'
                    secondary="If an alert is triggered with the same deduplication key as an existing open alert, it will be marked as a duplicate and suppressed. However, if the existing alert has been open longer than the configured expiry time, the system will release the old alert's key and treat the new signal as a fresh alert."
                  />
                  <div className={classes.inputAction}>
                    <div className={classes.inputRow}>
                      <TextField
                        className={classes.inputItem}
                        fullWidth
                        select
                        size='small'
                        value={displayValue}
                        onChange={(e) => handleDropdownChange(e.target.value)}
                        error={
                          updateMsg.includes('error') ||
                          updateMsg.includes('Error')
                        }
                      >
                        <MenuItem value={60}>1 Hour</MenuItem>
                        <MenuItem value={360}>6 Hours</MenuItem>
                        <MenuItem value={720}>12 Hours (Default)</MenuItem>
                        <MenuItem value={1440}>24 Hours</MenuItem>
                        <MenuItem value={10080}>1 Week</MenuItem>
                        <MenuItem value='custom'>Custom</MenuItem>
                      </TextField>
                      {isCustom ? (
                        <TextField
                          className={classes.inputItem}
                          fullWidth
                          type='number'
                          size='small'
                          label='Minutes'
                          value={customValue}
                          onChange={(e) => handleCustomChange(e.target.value)}
                          inputProps={{ min: 1, max: 90000 }}
                        />
                      ) : (
                        <div className={classes.inputItem} />
                      )}
                    </div>
                    {updateMsg && (
                      <Typography
                        variant='caption'
                        color={
                          updateMsg.includes('error') ||
                            updateMsg.includes('Error')
                            ? 'error'
                            : 'textSecondary'
                        }
                        style={{ marginTop: '0.5em' }}
                      >
                        {updateMsg}
                      </Typography>
                    )}
                  </div>
                </div>
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12}>
        <Accordion
          expanded={expanded === 'rules'}
          onChange={() => setExpanded(expanded === 'rules' ? false : 'rules')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>
              Notification Rules
            </Typography>
            <Typography className={classes.secondaryHeading}>
              Configure how the service sends notifications for alerts.
            </Typography>
            {rulesChanged && (
              <Chip
                className={classes.changeChip}
                size='small'
                label='Unsaved changes'
              />
            )}
          </AccordionSummary>
          <Divider />
          <AccordionDetails className={classes.accordionDetails}>
            <ServiceNotificationRuleList
              serviceID={serviceID}
              rules={currentRules}
              onCreate={handleRuleAdd}
              onDelete={handleRuleDelete}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12}>
        <Accordion
          expanded={expanded === 'auto-close'}
          onChange={() =>
            setExpanded(expanded === 'auto-close' ? false : 'auto-close')
          }
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>
              Auto-Close Configuration
            </Typography>
            <Typography className={classes.secondaryHeading}>
              Configure when alerts are automatically closed.
            </Typography>
            {(stagedAutoCloseStrategy !== null ||
              stagedAutoCloseThreshold !== null) && (
                <Chip
                  className={classes.changeChip}
                  size='small'
                  label='Unsaved changes'
                />
              )}
          </AccordionSummary>
          <Divider />
          <AccordionDetails className={classes.accordionDetails}>
            <List>
              <ListItem
                className={classes.listItem}
                sx={{
                  bgcolor:
                    stagedAutoCloseStrategy !== null
                      ? 'action.selected'
                      : 'transparent',
                }}
              >
                <div style={{ width: '100%' }}>
                  <ListItemText
                    className={classes.listItemText}
                    primary='Auto-Close Strategy'
                    secondary='Determines when alerts are automatically closed.'
                  />
                  <div className={classes.inputAction}>
                    <div className={classes.inputRow}>
                      <TextField
                        className={classes.inputItem}
                        fullWidth
                        select
                        size='small'
                        value={
                          stagedAutoCloseStrategy !== null
                            ? stagedAutoCloseStrategy
                            : data?.service?.autoCloseStrategy || 'ALWAYS'
                        }
                        onChange={(e) =>
                          handleAutoCloseStrategyChange(e.target.value)
                        }
                      >
                        <MenuItem value='ALWAYS'>
                          Always (Close immediately when resolved)
                        </MenuItem>
                        <MenuItem value='NEVER'>
                          Never (Manually close only)
                        </MenuItem>
                        <MenuItem value='THRESHOLD'>
                          Threshold (Close if open for longer than...)
                        </MenuItem>
                      </TextField>
                    </div>
                  </div>
                </div>
              </ListItem>
              {(stagedAutoCloseStrategy === 'THRESHOLD' ||
                (stagedAutoCloseStrategy === null &&
                  data?.service?.autoCloseStrategy === 'THRESHOLD')) && (
                  <ListItem
                    className={classes.listItem}
                    sx={{
                      bgcolor:
                        stagedAutoCloseThreshold !== null
                          ? 'action.selected'
                          : 'transparent',
                    }}
                  >
                    <div style={{ width: '100%' }}>
                      <ListItemText
                        className={classes.listItemText}
                        primary='Auto-Close Threshold'
                        secondary='Alerts will be automatically closed if they remain open for this duration.'
                      />
                      <div className={classes.inputAction}>
                        <div className={classes.inputRow}>
                          <DurationField
                            label='Threshold'
                            name='autoCloseThreshold'
                            value={
                              stagedAutoCloseThreshold !== null
                                ? stagedAutoCloseThreshold
                                : data?.service?.autoCloseThreshold || 'P3D'
                            }
                            onChange={handleAutoCloseThresholdChange}
                          />
                        </div>
                      </div>
                    </div>
                  </ListItem>
                )}
            </List>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12}>
        <SlackChannelSection
          value={currentSlackChannelID}
          onChange={(val) =>
            setStagedSlackChannelID(
              val === (data?.service?.slackChannelID || '') ? null : val,
            )
          }
        />
      </Grid>

      <Grid item xs={12}>
        <SlackListSection
          serviceID={serviceID}
          slackLists={data?.service?.slackLists || []}
        />
      </Grid>
    </Grid>
  )
}

function SlackListSection({
  serviceID,
  slackLists,
}: {
  serviceID: string
  slackLists: any[]
}) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)

  // Dialog states
  const [create, setCreate] = useState(false)
  const [editListID, setEditListID] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{
    id: string
    name: string
  } | null>(null)

  const list = slackLists[0]
  const enabled = Boolean(list)

  const handleToggle = () => {
    if (enabled) {
      // If enabled, toggle OFF means delete the existing list
      setDeleteDialog({ id: list.id, name: list.name })
    } else {
      // If disabled, toggle ON means create a new list
      setCreate(true)
    }
  }

  return (
    <React.Fragment>
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.heading}>Slack List</Typography>
          <Typography className={classes.secondaryHeading}>
            Sync internal alerts to a Slack List for task tracking.
          </Typography>
        </AccordionSummary>
        <Divider />
        <AccordionDetails className={classes.accordionDetails}>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText
                className={classes.listItemText}
                primary='Enable Slack List'
                secondary='Automatically creates tasks in Slack when alerts are triggered.'
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={handleToggle}
                    color='primary'
                  />
                }
                label={enabled ? 'Enabled' : 'Disabled'}
                labelPlacement='start'
              />
            </ListItem>
            {enabled && (
              <ListItem className={classes.listItem} sx={{ pl: 4 }}>
                <ListItemText
                  primary={list.name}
                  secondary={`Slack List ID: ${list.slackListID}`}
                />
                <IconButton onClick={() => setEditListID(list.id)}>
                  <Edit />
                </IconButton>
              </ListItem>
            )}
          </List>
        </AccordionDetails>
      </Accordion>
      {create && (
        <ServiceSlackListCreateDialog
          serviceID={serviceID}
          onClose={() => setCreate(false)}
        />
      )}
      {editListID && (
        <ServiceSlackListEditDialog
          listID={editListID}
          onClose={() => setEditListID(null)}
        />
      )}
      {deleteDialog && (
        <ServiceSlackListDeleteDialog
          serviceID={serviceID}
          id={deleteDialog.id}
          name={deleteDialog.name}
          onClose={() => setDeleteDialog(null)}
        />
      )}
    </React.Fragment>
  )
}

function SlackChannelSection(props: {
  value: string
  onChange: (val: string) => void
}) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)

  const [{ data, fetching }] = useQuery({
    query: channelNameQuery,
    variables: { id: props.value },
    pause: !props.value,
  })

  let content
  if (props.value) {
    const name =
      data?.slackChannel?.name || (fetching ? 'Loading...' : 'Unknown')
    content = (
      <ListItem className={classes.listItem} sx={{ pl: 0 }}>
        <ListItemText primary={name} secondary={`Service ID: ${props.value}`} />
        <IconButton
          onClick={() => props.onChange('')}
          title='Remove Slack Channel'
          size='small'
        >
          <Delete />
        </IconButton>
      </ListItem>
    )
  } else {
    content = (
      <ListItem className={classes.listItem}>
        <div className={classes.inputAction}>
          <div className={classes.inputRow}>
            <SlackChannelSelect
              fullWidth
              label='Channel'
              value={props.value}
              onChange={(val: string | string[] | null) =>
                props.onChange(val as string)
              }
            />
          </div>
        </div>
      </ListItem>
    )
  }

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography className={classes.heading}>Slack Channel</Typography>
        <Typography className={classes.secondaryHeading}>
          Send a notification to a specific Slack channel for every new alert.
        </Typography>
      </AccordionSummary>
      <Divider />
      <AccordionDetails className={classes.accordionDetails}>
        <List>
          {!props.value && (
            <ListItem className={classes.listItem}>
              <ListItemText
                className={classes.listItemText}
                primary='Service Slack Channel'
                secondary='The configured channel will receive a message whenever an alert is created for this service. Subsequent status updates (Sync/Ack/Close) will update the original message.'
              />
            </ListItem>
          )}
          {content}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}
